import { courseSessionsService } from './courseSessionsService';
import {
  getSessionStartDate,
  isSafePassCourse,
  isWithinProcessNumberAlertWindow,
  PROCESS_NUMBER_HOURS_THRESHOLD,
  sessionNeedsProcessAction,
  sessionOverlapsDateRange,
  todayIsoDate,
} from '../utils/safePassTracking';
import type { CourseSession, CourseSessionAttendee } from '../types/crm';

/** Safe Pass sessions whose first date falls within the range — no attendees loaded. */
export async function loadSafePassSessionsInDateRange(
  fromIso: string,
  toIso: string
): Promise<CourseSession[]> {
  const all = await courseSessionsService.listSessions();
  return all
    .filter((s) => isSafePassCourse(s.courseName) && s.status !== 'cancelled')
    .filter((s) => sessionOverlapsDateRange(s, fromIso, toIso))
    .sort((a, b) => (getSessionStartDate(a) || '').localeCompare(getSessionStartDate(b) || ''));
}

export async function loadBookedAttendeeCounts(
  sessionIds: string[]
): Promise<Record<string, number>> {
  return courseSessionsService.listBookedAttendeeCountsBySessionIds(sessionIds);
}

/** Attendees for sessions within the 96h alert window only (for urgent highlighting). */
export async function loadUrgentSessionIds(
  sessions: CourseSession[],
  now = new Date()
): Promise<Set<string>> {
  const candidates = sessions.filter((s) => isWithinProcessNumberAlertWindow(s, now));
  if (!candidates.length) return new Set();

  const attendeesBySession = await courseSessionsService.listAttendeesBySessionIds(
    candidates.map((s) => s.id)
  );
  const urgent = new Set<string>();
  candidates.forEach((session) => {
    if (sessionNeedsProcessAction(session, attendeesBySession[session.id] || [], now)) {
      urgent.add(session.id);
    }
  });
  return urgent;
}

export async function loadSessionParticipants(
  sessionId: string
): Promise<CourseSessionAttendee[]> {
  const rows = await courseSessionsService.listAttendees(sessionId);
  // Include virtual/reserved seats so process numbers can be issued before names are known.
  return rows.filter((a) => a.status === 'booked' || a.status === 'completed');
}

/** Sidebar badge — only scans sessions starting within the next 96 hours. */
export async function countUrgentProcessNumberSessions(): Promise<number> {
  try {
    const from = todayIsoDate();
    const toDate = new Date();
    toDate.setDate(toDate.getDate() + Math.ceil(PROCESS_NUMBER_HOURS_THRESHOLD / 24));
    const to = toDate.toISOString().slice(0, 10);
    const sessions = await loadSafePassSessionsInDateRange(from, to);
    const urgentIds = await loadUrgentSessionIds(sessions);
    return urgentIds.size;
  } catch (error) {
    console.error('countUrgentProcessNumberSessions:', error);
    return 0;
  }
}
